import {
	Box,
	Divider,
	FormControl,
	FormHelperText,
	IconButton,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
	QueryStatus,
	SessionState,
	SettingsObject,
	getExemptions,
	postAction,
} from "../utils";
import { Restore, Save } from "@mui/icons-material";

interface DetailsExemptionFieldsProps {
	id: string;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setPostStatus: (arg0: QueryStatus) => void;
	settings: SettingsObject;
}

export function DetailsExemptionFields({
	id,
	sessionState,
	setSessionState,
	setPostStatus,
	settings,
}: DetailsExemptionFieldsProps) {
	const exemptionsQuery = useQuery({
		queryKey: ["exemptions", id],
		queryFn: () =>
			getExemptions(
				{
					archived: false,
					caseIds: [id],
				},
				sessionState,
				setSessionState
			),
		enabled: true,
	});

	const {
		application = settings.exemptionApplicationTypes[0],
		status = settings.exemptionStatusTypes[0],
		submitted,
	} = exemptionsQuery?.data?.[id] || {};

	const [newApplication, setNewApplication] = useState(application);
	const [newSubmitted, setNewSubmitted] = useState<string | undefined>(
		submitted
	);
	const [newStatus, setNewStatus] = useState(status);

	const isEdited =
		application !== newApplication ||
		status !== newStatus ||
		submitted !== newSubmitted;

	const resetExemptionFields = () => {
		setNewApplication(application);
		setNewSubmitted(submitted);
		setNewStatus(status);
	};

	const updateExemption = () => {
		setPostStatus(QueryStatus.LOADING);
		postAction(
			{
				action: "edit-exemption",
				caseId: id,
				data: {
					application: newApplication,
					status: newStatus,
					submitted: newSubmitted,
				},
			},
			sessionState,
			setSessionState,
			() => {
				setPostStatus(QueryStatus.SUCCESS);
				exemptionsQuery.refetch();
			},
			(e) => {
				console.error(e);
				setPostStatus(QueryStatus.FAILURE);
			}
		);
	};

	useEffect(() => {
		resetExemptionFields();
	}, [application, status, submitted]);

	return (
		<Box
			sx={{
				m: 1,
				background: (theme) => theme.palette.grey[900],
				borderRadius: "8px",
			}}
		>
			<Typography
				sx={{
					p: 2,
					color: (theme) => theme.palette.grey[600],
				}}
			>
				Exemption Fields
			</Typography>
			<Divider />
			<Box
				sx={{
					display: "flex",
				}}
			>
				<FormControl
					sx={{
						m: 2,
						minWidth: 250,
						marginRight: 0,
					}}
				>
					<Select
						fullWidth
						value={newApplication}
						size="small"
						onChange={(event) =>
							setNewApplication(event.target.value)
						}
					>
						{settings.exemptionApplicationTypes.map((type) => (
							<MenuItem key={type} value={type}>
								{type}
							</MenuItem>
						))}
					</Select>
					<FormHelperText>Application</FormHelperText>
				</FormControl>
				<FormControl
					sx={{
						m: 2,
						minWidth: 250,
						marginRight: 0,
					}}
				>
					<DatePicker
						value={newSubmitted ? dayjs(newSubmitted) : null}
						slotProps={{
							textField: {
								size: "small",
								fullWidth: true,
							},
						}}
						onChange={(date) =>
							setNewSubmitted(date?.toISOString())
						}
					/>
					<FormHelperText>Submitted</FormHelperText>
				</FormControl>
				<FormControl sx={{ m: 2, minWidth: 250 }}>
					<Select
						fullWidth
						value={status}
						size="small"
						onChange={(event) => setNewStatus(event.target.value)}
					>
						{settings.exemptionStatusTypes.map((type) => (
							<MenuItem key={type} value={type}>
								{type}
							</MenuItem>
						))}
					</Select>
					<FormHelperText>Status</FormHelperText>
				</FormControl>
				<Box sx={{ display: "flex" }}>
					<IconButton
						onClick={() => updateExemption()}
						disabled={!isEdited}
					>
						<Save />
					</IconButton>
					<IconButton
						onClick={() => resetExemptionFields()}
						disabled={!isEdited}
					>
						<Restore />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
}
